import { useBrokerDetailsStore, FormField } from '../state/brokerDetailsStore';
import { BrokerStatusType } from '@apply/constants';

type StoreState = ReturnType<typeof useBrokerDetailsStore.getState>;

const currentSelectors: Record<FormField, (s: StoreState) => string | Date | undefined> = {
    // Business address
    [FormField.BusinessStreet1]: (s) => s.businessAddress.street1,
    [FormField.BusinessStreet2]: (s) => s.businessAddress.street2,
    [FormField.BusinessCity]: (s) => s.businessAddress.city,
    [FormField.BusinessAddressState]: (s) => s.businessAddress.state,
    [FormField.BusinessZipCode]: (s) => s.businessAddress.zipCode,
    // Contact info
    [FormField.PhoneNumberA]: (s) => s.phoneNumberA,
    [FormField.PhoneNumberAType]: (s) => s.phoneNumberAType,
    [FormField.Email]: (s) => s.email,
    [FormField.SecondaryEmail]: (s) => s.secondaryEmail,
    // Home address
    [FormField.HomeStreet1]: (s) => s.homeAddress.street1,
    [FormField.HomeStreet2]: (s) => s.homeAddress.street2,
    [FormField.HomeCity]: (s) => s.homeAddress.city,
    [FormField.HomeAddressState]: (s) => s.homeAddress.state,
    [FormField.HomeZipCode]: (s) => s.homeAddress.zipCode,
    // Personal
    [FormField.DateOfBirth]: (s) => s.dateOfBirth,
    [FormField.FirstName]: (s) => s.firstName,
    [FormField.LastName]: (s) => s.lastName,
    [FormField.ArizonaLicenseNumber]: (s) => s.arizonaLicenseNumber,
    // Banking
    [FormField.WireBankName]: (s) => s.wireBankName,
    [FormField.WireRecipientName]: (s) => s.wireRecipientName,
    [FormField.WireAccountLastFourDigits]: (s) => s.wireAccountLastFourDigits,
    // Company
    [FormField.BusinessName]: (s) => s.businessName,
    [FormField.BusinessState]: (s) => s.businessState,
    [FormField.Title]: (s) => s.title,
};

const reviewedSelectors: Record<FormField, (s: StoreState) => string | Date | undefined> = {
    // Business address
    [FormField.BusinessStreet1]: (s) => s.reviewedSnapshot?.businessAddress?.street1,
    [FormField.BusinessStreet2]: (s) => s.reviewedSnapshot?.businessAddress?.street2,
    [FormField.BusinessCity]: (s) => s.reviewedSnapshot?.businessAddress?.city,
    [FormField.BusinessAddressState]: (s) => s.reviewedSnapshot?.businessAddress?.state,
    [FormField.BusinessZipCode]: (s) => s.reviewedSnapshot?.businessAddress?.zipCode,
    // Contact info
    [FormField.PhoneNumberA]: (s) => s.reviewedSnapshot?.phoneNumberA,
    [FormField.PhoneNumberAType]: (s) => s.reviewedSnapshot?.phoneNumberAType,
    [FormField.Email]: (s) => s.reviewedSnapshot?.email,
    [FormField.SecondaryEmail]: (s) => s.reviewedSnapshot?.secondaryEmail,
    // Home address
    [FormField.HomeStreet1]: (s) => s.reviewedSnapshot?.homeAddress?.street1,
    [FormField.HomeStreet2]: (s) => s.reviewedSnapshot?.homeAddress?.street2,
    [FormField.HomeCity]: (s) => s.reviewedSnapshot?.homeAddress?.city,
    [FormField.HomeAddressState]: (s) => s.reviewedSnapshot?.homeAddress?.state,
    [FormField.HomeZipCode]: (s) => s.reviewedSnapshot?.homeAddress?.zipCode,
    // Personal
    [FormField.DateOfBirth]: (s) => s.reviewedSnapshot?.dateOfBirth,
    [FormField.FirstName]: (s) => s.reviewedSnapshot?.firstName,
    [FormField.LastName]: (s) => s.reviewedSnapshot?.lastName,
    [FormField.ArizonaLicenseNumber]: (s) => s.reviewedSnapshot?.arizonaLicenseNumber,
    // Banking
    [FormField.WireBankName]: (s) => s.reviewedSnapshot?.wireBankName,
    [FormField.WireRecipientName]: (s) => s.reviewedSnapshot?.wireRecipientName,
    [FormField.WireAccountLastFourDigits]: (s) => s.reviewedSnapshot?.wireAccountLastFourDigits,
    // Company
    [FormField.BusinessName]: (s) => s.reviewedSnapshot?.businessName,
    [FormField.BusinessState]: (s) => s.reviewedSnapshot?.businessState,
    [FormField.Title]: (s) => s.reviewedSnapshot?.title,
};

export const useBrokerFieldFieldChanged = (field: FormField): boolean => {
    const currentValue = useBrokerDetailsStore(currentSelectors[field]);
    const reviewedValue = useBrokerDetailsStore(reviewedSelectors[field]);
    const brokerStatus = useBrokerDetailsStore((s) => s.brokerStatus);
    const hasSnapshot = useBrokerDetailsStore((s) => s.reviewedSnapshot !== null);

    if (!hasSnapshot) return false;
    if (brokerStatus !== BrokerStatusType.Pending && brokerStatus !== BrokerStatusType.InviteAccepted) return false;
    if (!currentValue) return false;

    if (field === FormField.DateOfBirth) {
        const currentDate = currentValue as Date;
        const reviewedDate = reviewedValue as Date | undefined;
        if (!reviewedDate) return true;
        return currentDate.getTime() !== reviewedDate.getTime();
    }

    return currentValue !== reviewedValue;
};
